<template>
    <div class="contents">
        <div class="relative">
            <button
                type="button"
                class="h-11 flex px-4 py-2.5 hover:bg-slate-100 transition-colors rounded border-slate-200 items-center text-slate-800 text-sm font-medium btn"
                :class="{
                    'border-slate-200 border shadow bg-white': hasBackground,
                    '': !hasBackground,
                }"
                @click="showMore = !showMore"
                v-click-outside="() => (showMore = false)"
            >
                <Icon icon="dots-vertical" class="text-slate-500" />
            </button>
            <transition name="fade">
                <div
                    class="absolute top-full right-0 z-20 mt-2"
                    v-if="showMore"
                >
                    <div
                        class="bg-white p-2 rounded-lg shadow flex w-42 items-start whitespace-nowrap"
                    >
                        <ul class="text-sm">
                            <li class="md:hidden flex">
                                <button
                                    class="px-2 py-1 text-left flex items-center gap-2"
                                    @click="openRedemptionCreateModal"
                                >
                                    <Icon icon="plus" svgClass="h-4 w-4" />
                                    <span>Add Redemption</span>
                                </button>
                            </li>
                            <li
                                v-if="!trip.is_shared"
                                class="hover:bg-slate-100"
                            >
                                <button
                                    class="px-2 py-1 text-left flex items-center gap-2"
                                    @click="openShareTripModal"
                                >
                                    <Icon
                                        icon="globe"
                                        :width="16"
                                        :height="16"
                                    />
                                    <span>Share</span>
                                </button>
                            </li>
                            <li v-else class="hover:bg-slate-100">
                                <button
                                    class="px-2 py-1 text-left flex items-center gap-2"
                                    @click="openShareTripModal"
                                >
                                    <Icon
                                        icon="globe-dashed"
                                        :width="16"
                                        :height="16"
                                    />
                                    <span>Unshare</span>
                                </button>
                            </li>
                            <li class="hover:bg-slate-100">
                                <button
                                    class="px-2 py-1 text-left flex items-center gap-2"
                                    @click="editTrip()"
                                >
                                    <Icon
                                        icon="edit"
                                        :width="16"
                                        :height="16"
                                    />
                                    <span>Edit</span>
                                </button>
                            </li>
                            <li class="hover:bg-slate-100">
                                <button
                                    class="px-2 py-1 text-left flex items-center gap-2"
                                    @click="showDeleteConfirmation = true"
                                >
                                    <Icon
                                        icon="trash"
                                        :width="16"
                                        :height="16"
                                    />
                                    <span>Delete</span>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </transition>
        </div>
        <ConfirmationDialog
            :show="showDeleteConfirmation"
            :dialog="`Are you sure you want to delete this trip? This action cannot be undone.`"
            icon="trash"
            iconColor="red"
            @cancel="showDeleteConfirmation = false"
            @confirm="deleteTrip(trip?.uuid)"
            :buttons="[
                {
                    type: 'cancel',
                    text: 'Cancel',
                },
                {
                    type: 'confirm',
                    text: 'Delete',
                    value: true,
                    classes: '!bg-red-500 text-white',
                },
            ]"
        >
            <template #title>
                <div class="flex items-center gap-3 self-stretch">
                    <div
                        class="flex items-center justify-center w-12 h-12 p-3 rounded-full bg-red-100"
                    >
                        <Icon icon="trash" :pathClass="'fill-[#EF4444]'" />
                    </div>
                    <span
                        class="text-[var(--text-icons-primary,#1E293B)] font-inter text-xl font-medium leading-7"
                    >
                        Delete Trip {{ trip?.title }}?
                    </span>
                </div>
            </template>
        </ConfirmationDialog>
        <Teleport to="body">
            <ShareTripModal
                :show="showShareModal"
                :trip="trip"
                :shareToken="trip.share_token"
                @close="closeShareTripModal"
            />
        </Teleport>
    </div>
</template>

<script setup>
import { useTripsStore } from '@/store/trips';
import { ref } from 'vue';
import ConfirmationDialog from '@/components/ui/confirmation-dialog/confirmation-dialog.vue';
import Icon from '@/components/ui/icon/icon.vue';
import ShareTripModal from './modals/share-trip-modal.vue';
import CreateRedemption from './modals/create-redemption.vue';

const props = defineProps({
    trip: {
        type: Object,
        required: true,
    },
    showLabel: {
        type: Boolean,
        default: true,
    },
    hasBackground: {
        type: Boolean,
        default: true,
    },
});

const emit = defineEmits(['tripDeleted']);
const tripsStore = useTripsStore();
const showMore = ref(false);
const showDeleteConfirmation = ref(false);
const showShareModal = ref(false);
const showRedemptionCreateModal = ref(false);

const editTrip = () => {
    tripsStore.setActiveTrip(props.trip);
    tripsStore.setShowTripModal(true);
};

const deleteTrip = async (uuid) => {
    await tripsStore.deleteTrip(uuid);
    showDeleteConfirmation.value = false;
    showMore.value = false;
};

const openShareTripModal = () => {
    tripsStore.setActiveTrip(props.trip);
    showShareModal.value = true;
};

const closeShareTripModal = () => {
    tripsStore.resetActiveTripData();
    showShareModal.value = false;
};

const openRedemptionCreateModal = () => {
    tripsStore.getTrip(props.trip.uuid);
    tripsStore.setShowRedemptionCreateModal(true);
};
</script>
